import React, { useEffect, useState } from 'react';
import { adminApi } from '../../services/api';
import { X, ArrowRight, Plus, Minus, FileDiff, Loader2 } from 'lucide-react';

/**
 * Modal that shows a section-by-section diff of a privacy policy version
 * against the previous (or against a chosen version). Used in AdminLocale →
 * "Storico versioni" so the admin / DPO can quickly review what changed
 * between two GDPR snapshots.
 *
 * Props:
 *   versionId: string  — id of the "to" version to inspect
 *   versionLabel: string — small label rendered in the header (e.g. "v3")
 *   onClose: () => void
 */
export default function PrivacyPolicyDiffModal({ versionId, versionLabel, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!versionId) return;
    setLoading(true);
    adminApi.get(`/admin/privacy-versions/${versionId}/diff`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Errore caricamento diff'))
      .finally(() => setLoading(false));
  }, [versionId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="privacy-diff-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
          <FileDiff size={18} className="text-[#D44E20]" />
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-[#1A1A1A]">Confronto Privacy Policy</h2>
            {data?.from && data?.to && (
              <p className="text-xs text-[#64748B] flex items-center gap-1.5 mt-0.5">
                <span className="font-mono font-semibold text-red-600">v{data.from.version}</span>
                <ArrowRight size={11} className="text-[#94A3B8]" />
                <span className="font-mono font-semibold text-emerald-600">v{data.to.version}</span>
                <span className="text-[#94A3B8]">· {versionLabel || ''}</span>
              </p>
            )}
            {data && !data.from && (
              <p className="text-xs text-amber-700 mt-0.5">Prima versione — nessun confronto precedente disponibile</p>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-[#64748B]" data-testid="privacy-diff-close-btn">
            <X size={16} />
          </button>
        </header>

        <main className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-[#D44E20]" />
            </div>
          )}
          {error && !loading && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>
          )}
          {data && !loading && (
            <>
              {/* Company snapshot diff */}
              {Object.keys(data.company_changes || {}).length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="company-changes-block">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800 mb-2">
                    Dati aziendali modificati
                  </p>
                  <div className="space-y-1.5">
                    {Object.entries(data.company_changes).map(([key, val]) => (
                      <div key={key} className="text-xs">
                        <span className="font-semibold text-[#1A1A1A]">{prettyField(key)}: </span>
                        <span className="line-through text-red-700">{val.old || '—'}</span>
                        <span className="text-[#94A3B8] mx-1.5">→</span>
                        <span className="text-emerald-700 font-medium">{val.new || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Section diffs */}
              {(data.sections || []).map((s, idx) => (
                <SectionDiff key={idx} section={s} idx={idx} />
              ))}

              {(data.sections || []).every(s => s.status === 'unchanged') && (
                <p className="text-xs text-[#64748B] text-center py-8" data-testid="no-changes-msg">
                  Nessuna modifica testuale tra le due versioni.
                </p>
              )}
            </>
          )}
        </main>

        <footer className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-[10px] text-[#94A3B8]">
          <span>Le righe <span className="text-red-700 font-semibold">rosse</span> sono state rimosse, quelle <span className="text-emerald-700 font-semibold">verdi</span> sono state aggiunte.</span>
          <button onClick={onClose} className="text-[11px] font-semibold text-[#D44E20] hover:underline">Chiudi</button>
        </footer>
      </div>
    </div>
  );
}


function SectionDiff({ section, idx }) {
  if (section.status === 'unchanged') {
    return null; // hide unchanged sections to keep diff focused
  }
  const badge =
    section.status === 'added' ? { text: 'NUOVA', cls: 'bg-emerald-100 text-emerald-700' } :
    section.status === 'removed' ? { text: 'RIMOSSA', cls: 'bg-red-100 text-red-700' } :
    { text: 'MODIFICATA', cls: 'bg-amber-100 text-amber-700' };

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`section-diff-${idx}`}>
      <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
        <span className={`text-[9px] font-black tracking-wider px-1.5 py-0.5 rounded ${badge.cls}`}>
          {badge.text}
        </span>
        <p className="text-xs font-bold text-[#1A1A1A] flex-1 min-w-0 truncate">{section.title}</p>
        {section.unchanged_count > 0 && (
          <span className="text-[10px] text-[#94A3B8]">{section.unchanged_count} righe invariate</span>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {section.removed.map((p, i) => (
          <div key={`r-${i}`} className="px-3 py-2 bg-red-50 flex items-start gap-2 text-xs">
            <Minus size={11} className="text-red-600 mt-1 flex-shrink-0" />
            <span className="text-red-900 whitespace-pre-wrap break-words">{p}</span>
          </div>
        ))}
        {section.added.map((p, i) => (
          <div key={`a-${i}`} className="px-3 py-2 bg-emerald-50 flex items-start gap-2 text-xs">
            <Plus size={11} className="text-emerald-600 mt-1 flex-shrink-0" />
            <span className="text-emerald-900 whitespace-pre-wrap break-words">{p}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function prettyField(key) {
  const map = {
    company_name: 'Ragione sociale',
    vat_number: 'P.IVA',
    legal_address: 'Sede legale',
    company_phone: 'Telefono',
    company_email: 'Email',
    business_name: 'Nome locale',
  };
  return map[key] || key;
}
